import React, { useState, useRef } from 'react';
import { useUser } from 'util/UserContext';
import AvatarEditor from 'react-avatar-editor';
import api from 'config/axiosConfig';
import Modal from 'ui/Modal';
import { X } from 'lucide-react';
import { getUploadAccept, validateUploadFile } from 'util/uploadValidation';
import { logger } from 'lib/logger';
import { apiRoutes } from "util/apiRoutes";

interface Props {
    onClose: () => void;
}

const UserAvatarModal: React.FC<Props> = ({ onClose }) => {
    const { currentUser } = useUser();
    const [image, setImage] = useState<File | null>(null);
    const [scale, setScale] = useState<number>(1);
    const [rotate, setRotate] = useState<number>(0);
    const [avatarKey, setAvatarKey] = useState<string | undefined>(
        currentUser?.properties_public?.avatar_key
    );
    const [fileError, setFileError] = useState<string | null>(null);
    const editorRef = useRef<AvatarEditor>(null);
    const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN;

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files?.[0]) {
            const nextFile = event.target.files[0];
            const validation = validateUploadFile(nextFile, "avatar");
            if (!validation.valid) {
                setFileError(validation.message);
                setImage(null);
                return;
            }
            setFileError(null);
            setImage(nextFile);
        }
    };

    const handleSave = async () => {
        if (editorRef.current) {
            editorRef.current.getImageScaledToCanvas().toBlob(async (blob) => {
                if (blob) {
                    const formData = new FormData();
                    formData.append('image', blob, 'profile-picture.png');

                    try {
                        setFileError(null);
                        const response = await api.post(apiRoutes.user.avatar, formData);
                        setAvatarKey(response.data.key);
                        setScale(1);
                        setRotate(0);
                        setImage(null);
                    } catch (error) {
                        logger.error("Avatar upload failed", { error });
                        setFileError("Failed to upload avatar. Please try again.");
                    }
                }
            }, 'image/png');
        }
    };

    const handleRemoveAvatar = async () => {
        try {
            setFileError(null);
            await api.patch(apiRoutes.user.avatar, {
                avatar_key: '',
                avatar_url: ''
            });
            setAvatarKey(undefined);
            setImage(null);
        } catch (error) {
            logger.error("Avatar removal failed", { error });
            setFileError("Failed to remove avatar. Please try again.");
        }
    };

    const handleClose = () => {
        onClose();
        window.location.reload();
    };

    const footer = (
        <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex sm:flex-row sm:justify-end sm:gap-x-2 py-2">
            {image ? (
                <>
                    <button
                        type="button"
                        onClick={() => setImage(null)}
                        className="btn-secondary w-full sm:w-fit py-2 px-4"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!image}
                        className={`w-full sm:w-fit py-2 px-4 ${
                            !image ? 'btn-primary-disabled' : 'btn-primary'
                        } ${!image ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        Save
                    </button>
                </>
            ) : (
                <button
                    type="button"
                    onClick={handleClose}
                    className="btn-secondary w-full sm:w-fit py-2 px-4"
                >
                    Close
                </button>
            )}
        </div>
    );

    return (
        <Modal isOpen={true} onClose={handleClose} title="Avatar image" footer={footer} stickyFooter>
            <div className="flex flex-col w-full sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                <div className="flex flex-col flex-grow space-y-4">
                    <div className="flex-1 flex items-center justify-center">
                        {!image ? (
                            <div className="avatar-container flex flex-col items-center space-y-2">
                                <div className="relative">
                                    {avatarKey && avatarKey.trim() !== '' ? (
                                        <img
                                            src={avatarKey && avatarKey.trim() !== '' ? `${IMAGE_CDN}/${avatarKey}` : currentUser?.username.charAt(0).toUpperCase() || undefined}
                                            alt={currentUser?.username.charAt(0).toUpperCase() || "Profile"}
                                            className="size-24 rounded-full border-4 border-eoe-gray object-cover"
                                        />
                                    ) : (
                                        <div className="size-24 rounded-full border-4 border-eoe-gray bg-eoe-bg dark:bg-eoe-dark-bg flex items-center justify-center">
                                            <span className="text-2xl text-eoe-text dark:text-eoe-dark-text">{currentUser?.username.charAt(0).toUpperCase() || "P"}</span>
                                        </div>
                                    )}
                                    {avatarKey && avatarKey.trim() !== '' && (
                                        <button
                                            onClick={handleRemoveAvatar}
                                            className="absolute -top-2 -right-1 p-1 bg-eoe-bg dark:bg-eoe-dark-bg rounded-full hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover"
                                            aria-label="Remove avatar"
                                        >
                                            <X className="w-4 h-4 text-eoe-text dark:text-eoe-dark-text" />
                                        </button>
                                    )}
                                </div>
                                {avatarKey && avatarKey.trim() !== '' && (
                                    <button
                                        onClick={handleRemoveAvatar}
                                        className="btn-secondary bg-warning-red hover:bg-dark-warning-red w-full sm:w-fit py-2 px-4"
                                    >
                                        Remove image
                                    </button>
                                )}
                                <label className="btn-primary flex items-center justify-center w-full sm:w-fit py-2 px-4 cursor-pointer">
                                    <span className="text-center">New image</span>
                                    <input
                                        type="file"
                                        hidden
                                        accept={getUploadAccept("avatar")}
                                        onChange={handleFileChange}
                                    />
                                </label>
                                {fileError && (
                                    <p className="text-sm text-warning-red dark:text-dark-warning-red">
                                        {fileError}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="avatar-editor-container flex flex-col items-center space-y-2">
                                <AvatarEditor
                                    ref={editorRef}
                                    image={image}
                                    border={0}
                                    color={[0, 0, 0, 0]}
                                    scale={scale}
                                    rotate={rotate}
                                    className="rounded-full border-4 border-eoe-gray"
                                />
                                <div className="max-w-full space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-eoe-text dark:text-eoe-dark-text">Zoom:&nbsp;</span>
                                        <input
                                            type="range"
                                            min={1}
                                            max={3}
                                            step={0.01}
                                            value={scale}
                                            onChange={(e) => setScale(Number(e.target.value))}
                                            className="max-w-full h-2 bg-eoe-border dark:bg-eoe-dark-border rounded-lg appearance-none cursor-pointer"
                                        />
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-eoe-text dark:text-eoe-dark-text">Rotate:</span>
                                        <input
                                            type="range"
                                            min={0}
                                            max={360}
                                            step={1}
                                            value={rotate}
                                            onChange={(e) => setRotate(Number(e.target.value))}
                                            className="max-w-full h-2 bg-eoe-border dark:bg-eoe-dark-border rounded-lg appearance-none cursor-pointer"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action buttons moved to Modal footer */}
            </div>
        </Modal>
    );
};

export default UserAvatarModal;